import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { cognitoConfig, isMockAuth, mockUser } from "./config";
import { deriveCodeChallenge, generateCodeVerifier, generateState } from "./pkce";
import {
  clearTokens,
  decodeUser,
  loadTokens,
  refreshTokens,
  saveTokens,
  TokenBundle,
} from "./tokens";

interface AuthUser {
  sub: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}

const MOCK_USER_STORAGE_KEY = "fretboard.auth.mock-user";

function loadMockUser(): AuthUser | null {
  const raw = localStorage.getItem(MOCK_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
  getIdToken: () => Promise<string | null>;
  completeSignIn: (tokens: TokenBundle) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const PKCE_STORAGE_KEY = "fretboard.auth.pkce";

interface PendingPkce {
  verifier: string;
  state: string;
  returnTo: string;
}

export function stashPkce(p: PendingPkce): void {
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(p));
}

export function takePkce(): PendingPkce | null {
  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
  try {
    return JSON.parse(raw) as PendingPkce;
  } catch {
    return null;
  }
}

function userFromTokens(t: TokenBundle): AuthUser {
  const c = decodeUser(t.idToken);
  return {
    sub: c.sub,
    email: c.email,
    givenName: c.given_name,
    familyName: c.family_name,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const tokensRef = useRef<TokenBundle | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const applyTokens = useCallback((t: TokenBundle | null) => {
    tokensRef.current = t;
    if (t) {
      saveTokens(t);
      setUser(userFromTokens(t));
    } else {
      clearTokens();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (isMockAuth) {
      const stored = loadMockUser();
      if (stored) setUser(stored);
    } else {
      const t = loadTokens();
      if (t) {
        try {
          applyTokens(t);
        } catch (e) {
          console.warn("Stored auth tokens failed to decode, clearing:", e);
          clearTokens();
        }
      }
    }
    setLoading(false);
  }, [applyTokens]);

  const signIn = useCallback(async () => {
    if (isMockAuth) {
      localStorage.setItem(MOCK_USER_STORAGE_KEY, JSON.stringify(mockUser));
      setUser(mockUser);
      return;
    }
    const verifier = generateCodeVerifier();
    const challenge = await deriveCodeChallenge(verifier);
    const state = generateState();
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    const path = window.location.pathname;
    const routerPath = base && path.startsWith(base) ? path.slice(base.length) : path;
    stashPkce({
      verifier,
      state,
      returnTo: (routerPath || "/") + window.location.search,
    });
    const params = new URLSearchParams({
      client_id: cognitoConfig.clientId,
      response_type: "code",
      scope: "openid email profile",
      redirect_uri: cognitoConfig.redirectUri,
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
      identity_provider: "Google",
    });
    window.location.assign(`${cognitoConfig.domain}/oauth2/authorize?${params}`);
  }, []);

  const signOut = useCallback(() => {
    if (isMockAuth) {
      localStorage.removeItem(MOCK_USER_STORAGE_KEY);
      setUser(null);
      return;
    }
    applyTokens(null);
    const params = new URLSearchParams({
      client_id: cognitoConfig.clientId,
      logout_uri: cognitoConfig.logoutUri,
    });
    window.location.assign(`${cognitoConfig.domain}/logout?${params}`);
  }, [applyTokens]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (isMockAuth) {
      return user ? `mock-token-${user.sub}` : null;
    }
    const current = tokensRef.current;
    if (!current) return null;
    if (Date.now() < current.expiresAt) return current.idToken;
    if (!current.refreshToken) {
      applyTokens(null);
      return null;
    }
    try {
      const refreshed = await refreshTokens(current.refreshToken);
      applyTokens(refreshed);
      return refreshed.idToken;
    } catch {
      applyTokens(null);
      return null;
    }
  }, [applyTokens, user]);

  const completeSignIn = useCallback((tokens: TokenBundle) => {
    applyTokens(tokens);
  }, [applyTokens]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signIn, signOut, getIdToken, completeSignIn }),
    [user, loading, signIn, signOut, getIdToken, completeSignIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

